/**
 * Sets a cookie in the browser with a specified name, value and expiration date.
 *
 * @param name - The name of the cookie.
 * @param value - The value of the cookie.
 * @param expiresAtUtc - UTC timestamp or ISO string defining when the cookie expires.
 * @param path - Path where cookie is valid (default: '/').
 */

export const setCookie = (
	name: string,
	value: string,
	expiresAtUtc?: number | string,
	path = "/"
) => {
	if (typeof document === "undefined") return;

	const cookieParts = [
		`${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
		`Path=${path}`,
		"SameSite=Lax",
	];

	if (expiresAtUtc !== undefined) {
		const expires = new Date(expiresAtUtc).toUTCString();
		cookieParts.push(`Expires=${expires}`);
	}

	const isLocalhost =
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1";
	if (window.location.protocol === "https:" && !isLocalhost) {
		cookieParts.push("Secure");
	}

	const cookieString = cookieParts.join("; ");

	document.cookie = cookieString;
};

/**
 * Retrieves a cookie by name.
 *
 * @param name - Cookie name to search for.
 * @returns The cookie value or null.
 */
export const getCookie = (name: string): string | null => {
	if (typeof document === "undefined") return null;

	const nameEQ = `${encodeURIComponent(name)}=`;
	const cookies = document.cookie.split(";");

	for (let cookie of cookies) {
		cookie = cookie.trim();
		if (cookie.startsWith(nameEQ)) {
			return decodeURIComponent(cookie.substring(nameEQ.length));
		}
	}

	return null;
};

/**
 * Removes a cookie by setting it expired.
 *
 * @param name - The cookie name.
 * @param path - Path where cookie was set.
 */
export const deleteCookie = (name: string, path = "/") => {
	if (typeof document === "undefined") return;

	document.cookie = [
		`${encodeURIComponent(name)}=`,
		`Path=${path}`,
		"Expires=Thu, 01 Jan 1970 00:00:00 GMT",
		"SameSite=Lax",
		"Secure",
	].join("; ");
};
